import axios from 'axios';
import { handleApiError } from '../../utils/errorHandler';

// Keys cleared on logout so user preferences are not visible when logged out (must match ThemeContext)
const THEME_STORAGE_KEY = 'theme-preference';
const LOGOUT_EVENT = 'app-logout';

function clearAuthAndUserPreferences() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_data');
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch (_) {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LOGOUT_EVENT));
  }
}

// Create axios instance with base configuration
const getBaseURL = () => {
  const envUrl = process.env.REACT_APP_API_URL || '';
  // If the URL doesn't end with /api, add it
  if (envUrl && !envUrl.endsWith('/api')) {
    return envUrl.endsWith('/') ? `${envUrl}api` : `${envUrl}/api`;
  }
  if (envUrl) return envUrl;
  // When no env set: use same origin so reverse proxy (e.g. /api -> backend:8000) works
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:8000/api';
};

const baseURL = getBaseURL();
console.log('API Base URL:', baseURL);

// Global refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;
let refreshFailureCount = 0;
const MAX_REFRESH_FAILURES = 3;

const api = axios.create({
  baseURL: baseURL,
  timeout: 60000, // Increased to 60 seconds for image uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token (skip if missing or corrupted e.g. "undefined")
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && token !== 'undefined' && typeof token === 'string') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData - let the browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Unwrap ApiResponse envelope (RULE §9): response.data.data -> response.data so callers get payload
function unwrapEnvelope(response) {
  const d = response?.data;
  if (d && typeof d === 'object' && d.success === true && 'data' in d) {
    response.data = d.data;
  }
  return response;
}

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => unwrapEnvelope(response),
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - only try to refresh token for authenticated users
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._isRefreshRequest) {
      const refreshToken = localStorage.getItem('refresh_token');
      const accessToken = localStorage.getItem('access_token');
      
      // Only attempt token refresh if user has both tokens (was previously authenticated)
      if (refreshToken && accessToken) {
        originalRequest._retry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        
        // Circuit breaker: if we've had too many refresh failures, stop trying
        if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
          console.log('Too many refresh failures, clearing tokens and stopping refresh attempts');
          clearAuthAndUserPreferences();
          refreshFailureCount = 0; // Reset counter
          // Don't redirect immediately, let the AuthContext handle it
          const appError = handleApiError(error);
          return Promise.reject(appError);
        }
        
        // Prevent infinite loops - max 2 retry attempts per request
        if (originalRequest._retryCount > 2) {
          console.log('Max retry attempts reached for this request, clearing tokens');
          clearAuthAndUserPreferences();
          // Don't redirect immediately, let the AuthContext handle it
          const appError = handleApiError(error);
          return Promise.reject(appError);
        }

        // If already refreshing, wait for the existing refresh to complete
        if (isRefreshing) {
          try {
            await refreshPromise;
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
            return api(originalRequest);
          } catch (refreshError) {
            const appError = handleApiError(error);
            return Promise.reject(appError);
          }
        }

        // Start refresh process
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            // Create a separate axios instance for refresh to avoid interceptor loop
            const refreshResponse = await axios.post(`${baseURL}/users/token/refresh/`, {
              refresh: refreshToken
            }, {
              timeout: 5000,
              headers: {
                'Content-Type': 'application/json',
              }
            });
            // Backend returns ApiResponse envelope { success, data: { access } }; raw axios doesn't unwrap
            const payload = refreshResponse.data?.data ?? refreshResponse.data;
            const access = payload?.access;
            if (!access || typeof access !== 'string') {
              throw new Error('Refresh response missing access token');
            }
            localStorage.setItem('access_token', access);
            refreshFailureCount = 0; // Reset failure count on success
            return access;
          } catch (refreshError) {
            // Refresh failed, increment failure count and clear auth + user preferences
            refreshFailureCount++;
            console.log(`Token refresh failed (${refreshFailureCount}/${MAX_REFRESH_FAILURES}), clearing stored tokens`);
            clearAuthAndUserPreferences();
            throw refreshError;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();

        try {
          await refreshPromise;
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Don't redirect immediately, let the AuthContext handle it
          const appError = handleApiError(error);
          return Promise.reject(appError);
        }
      } else {
        // No tokens found - user is browsing public content
        // Don't log error for expected 401 on authenticated-only endpoints
        const url = originalRequest.url || '';
        const isAuthRequiredEndpoint = url.includes('/cart/') || url.includes('/wishlist/');
        
        if (!isAuthRequiredEndpoint) {
          console.log('No authentication tokens found, treating as public request');
        }
        // Don't convert to error for auth-required endpoints when not logged in
        if (isAuthRequiredEndpoint) {
          return Promise.reject({ 
            message: 'Authentication required', 
            isAuthRequired: true,
            status: 401 
          });
        }
      }
    }

    // Convert axios error to our custom error format
    const appError = handleApiError(error);
    return Promise.reject(appError);
  }
);

export default api;
